// components/Admin/QuizList.tsx
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { db } from '../../firebase';
import { Quiz } from '../../types/quiz.types';
import { Crew } from '../../types/presenter.types';
import { useQuiz } from '../../hooks/useQuiz';
import { useAuth } from '../../context/Auth/auth-context';
import { canHostQuiz, listCrews } from '../../services/presenter';
import { ShareQuizModal } from './ShareQuizModal';

interface Session {
  quizId: string;
  hostUid?: string;
}

export const QuizList = () => {
  const [quizzes, setQuizzes] = useState<Record<string, Quiz>>({});
  const [sessions, setSessions] = useState<Record<string, Session>>({});
  const [crews, setCrews] = useState<Record<string, Crew>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [newCode, setNewCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [shareQuizId, setShareQuizId] = useState<string | null>(null);
  const { createSession, setQuizCatalog, loading } = useQuiz();
  const { user, userInfo } = useAuth();
  const navigate = useNavigate();

  const isAdmin = userInfo?.role === 'admin';

  useEffect(() => {
    const quizzesRef = ref(db, 'quizzes');
    return onValue(quizzesRef, (snapshot) => setQuizzes(snapshot.val() || {}));
  }, []);

  useEffect(() => {
    const sessionsRef = ref(db, 'sessions');
    return onValue(sessionsRef, (snapshot) => setSessions(snapshot.val() || {}));
  }, []);

  // One-time get, not a listener (crews change rarely): needed to resolve
  // which crew-shared quizzes this presenter can host.
  useEffect(() => {
    listCrews().then(setCrews).catch(() => {});
  }, []);

  // Presenters see how often THEY hosted a quiz; admins see the global count.
  const getSessionCount = (quizId: string) =>
    Object.values(sessions).filter(
      (s) => s.quizId === quizId && (isAdmin || s.hostUid === user)
    ).length;

  // Host = create the game right away (app mode by default). Going live on
  // YouTube is a per-session setting, set later from SessionManager → Settings.
  const handleHost = async (quizId: string) => {
    if (!user) return;
    const sessionCode = await createSession(quizId, user);
    if (sessionCode) setNewCode(sessionCode);
  };

  const copyPin = () => {
    if (!newCode) return;
    navigator.clipboard?.writeText(newCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Visibility mirrors the rules: an admin manages everything; a presenter
  // hosts their own quizzes + the shared catalog + quizzes shared with them
  // (directly or via a crew) — all of which they can never edit (rules reject
  // the write, the UI just doesn't offer it).
  const visibleQuizzes = Object.entries(quizzes).filter(
    ([, quiz]) => isAdmin || (user && canHostQuiz(quiz, user, crews))
  );

  const filteredQuizzes = visibleQuizzes.filter(([, quiz]) =>
    quiz.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const mine = filteredQuizzes.filter(([, q]) => isAdmin || q.createdBy === user);
  const catalogOnly = isAdmin
    ? []
    : filteredQuizzes.filter(([, q]) => q.createdBy !== user && q.catalog === true);
  // Shared ⊄ catalog: quizzes another presenter shared with me (directly or
  // through one of my crews) that aren't already surfaced above.
  const sharedOnly = isAdmin
    ? []
    : filteredQuizzes.filter(
        ([, q]) => q.createdBy !== user && q.catalog !== true
      );

  // Role not loaded yet — don't flash an empty/misfiltered list.
  if (!userInfo?.role) {
    return <div className="max-w-3xl mx-auto p-6 text-slate-400">Loading…</div>;
  }

  const quizCard = ([quizId, quiz]: [string, Quiz], readOnly: boolean) => {
    const canShare = isAdmin || quiz.createdBy === user;
    const isShared = Boolean(quiz.crewId || Object.keys(quiz.sharedWith ?? {}).length);
    return (
      <div key={quizId} className="card p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-lg font-bold truncate">{quiz.title || 'Untitled quiz'}</h3>
            {quiz.catalog === true && (
              <span className="chip bg-accent/30 text-ink shrink-0">CATALOG</span>
            )}
            {isShared && (
              <span
                className="chip bg-tile-blue/15 text-tile-blue shrink-0"
                title={quiz.crewId && crews[quiz.crewId] ? `Shared with crew "${crews[quiz.crewId].name}"` : 'Shared with presenters'}
              >
                SHARED
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500">
            {Object.keys(quiz.questions || {}).length} questions · {getSessionCount(quizId)} sessions
            {readOnly && ' · read-only'}
          </p>
        </div>
        <div className="flex gap-2 shrink-0 items-center">
          {isAdmin && (
            <button
              onClick={() => setQuizCatalog(quizId, quiz.catalog !== true)}
              className="btn text-sm text-ink bg-ink/10 hover:bg-ink/15"
              title={quiz.catalog ? 'Remove from the shared presenter catalog' : 'Share with all presenters (read-only for them)'}
            >
              {quiz.catalog ? 'Uncatalog' : 'Catalog'}
            </button>
          )}
          {canShare && (
            <button
              onClick={() => setShareQuizId(quizId)}
              className="btn text-sm text-ink bg-ink/10 hover:bg-ink/15"
              title="Share with a crew or individual presenters (they can host it, never edit it)"
            >
              Share
            </button>
          )}
          <button onClick={() => handleHost(quizId)} disabled={loading} className="btn-success text-sm flex-1 sm:flex-none">
            Host live
          </button>
          <Link to={`/admin/quizzes/${quizId}/sessions`} className="btn text-sm flex-1 sm:flex-none text-ink bg-ink/10 hover:bg-ink/15">
            Sessions
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-extrabold font-display tracking-tight">
          {isAdmin ? 'All quizzes' : 'Your quizzes'}
        </h1>
        <Link to="/admin/create" className="btn-primary text-sm">+ New quiz</Link>
      </div>

      <input
        type="text"
        placeholder="Search quizzes…"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full p-3 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none mb-5"
      />

      <div className="space-y-3">
        {filteredQuizzes.length === 0 && (
          <p className="text-slate-400 text-center py-10">No quizzes yet — create one to get started.</p>
        )}
        {mine.map((entry) => quizCard(entry, false))}
      </div>

      {sharedOnly.length > 0 && (
        <>
          <h2 className="text-lg font-extrabold font-display tracking-tight mt-8 mb-3">
            Shared with you
            <span className="ml-2 text-sm font-normal text-slate-400">by other presenters or your crews — host as-is</span>
          </h2>
          <div className="space-y-3">
            {sharedOnly.map((entry) => quizCard(entry, true))}
          </div>
        </>
      )}

      {catalogOnly.length > 0 && (
        <>
          <h2 className="text-lg font-extrabold font-display tracking-tight mt-8 mb-3">
            Catalog
            <span className="ml-2 text-sm font-normal text-slate-400">host these as-is — only admins can edit them</span>
          </h2>
          <div className="space-y-3">
            {catalogOnly.map((entry) => quizCard(entry, true))}
          </div>
        </>
      )}

      {shareQuizId && quizzes[shareQuizId] && user && (
        <ShareQuizModal
          quizId={shareQuizId}
          quiz={quizzes[shareQuizId]}
          myUid={user}
          isAdmin={isAdmin}
          onClose={() => setShareQuizId(null)}
        />
      )}

      {/* Session-created modal (replaces the old window.alert) */}
      {newCode && (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={() => setNewCode(null)}>
          <div className="card w-full max-w-sm p-8 text-center animate-pop" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold font-display tracking-tight">Game created</h2>
            <p className="text-slate-500 text-sm mt-1">Share this PIN with your audience.</p>
            <div className="text-6xl font-display font-extrabold text-ink tracking-[0.15em] my-5 tabular-nums">{newCode}</div>
            <div className="flex gap-2">
              <button onClick={copyPin} className="btn-ghost flex-1 !text-ink !bg-ink/10 hover:!bg-ink/15">
                {copied ? '✓ Copied' : 'Copy PIN'}
              </button>
              <button onClick={() => navigate(`/admin/session/${newCode}`)} className="btn-primary flex-1">
                Open game
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
